/**
 * Gift a Moment — single-page 6-step state machine.
 *
 * Flow: recipient → intention → personal line → affirmations → music → preview/share
 *
 * Visual language: Private Suite — warm violet/gold, generous negative
 * space, soft gradients. Aim: "a sacred little gift".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Copy, Heart,
  Loader2, Pencil, Play, Pause, Plus, Share2, Sparkles, Trash2, X,
} from "lucide-react";
import { VoiceChoice, RecordScreen } from "./GiftVoiceStep";

const API = process.env.REACT_APP_BACKEND_URL;
const VIOLET = "#8B7ED4";
const GOLD = "#CBA153";

const token = () => { try { return localStorage.getItem("reclaim_token") || ""; } catch (_) { return ""; } };

const fetchJson = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.auth !== false ? { Authorization: `Bearer ${token()}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    let detail = "";
    try { const j = await r.json(); detail = j?.detail || ""; } catch (_) {}
    const err = new Error(`${r.status}${detail ? ` · ${detail}` : ""}`);
    err.status = r.status;
    throw err;
  }
  return r.json();
};

// ─── Shared layout ──────────────────────────────
const GiftFrame = ({ eyebrow, title, onBack, onClose, children }) => (
  <div className="min-h-screen relative overflow-hidden" style={{ background: "#0a0a12" }} data-testid="gift-frame">
    {/* Soft ambient violets */}
    <motion.div
      className="absolute -top-40 -right-20 w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{ background: `radial-gradient(circle, ${VIOLET}22 0%, transparent 65%)`, filter: "blur(90px)" }}
      animate={{ opacity: [0.35, 0.55, 0.35] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-40 -left-20 w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{ background: `radial-gradient(circle, ${GOLD}14 0%, transparent 65%)`, filter: "blur(90px)" }}
      animate={{ opacity: [0.25, 0.45, 0.25] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
    />
    <div className="relative z-10 flex items-center justify-between px-6 pt-6">
      <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-white/5" aria-label="Back" data-testid="gift-back">
        <ArrowLeft size={22} color="rgba(245,240,230,0.75)" />
      </button>
      <div className="flex flex-col items-center">
        <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: `${VIOLET}99` }}>{eyebrow}</p>
        {title && (
          <p className="text-sm mt-1" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.78)" }}>
            {title}
          </p>
        )}
      </div>
      <button onClick={onClose} className="text-[10px] uppercase tracking-[0.3em] p-2 -mr-2 rounded-full hover:bg-white/5"
              style={{ color: `${VIOLET}99` }} data-testid="gift-close">Close</button>
    </div>
    <div className="relative z-10">{children}</div>
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, testId, loading }) => (
  <button
    disabled={disabled || loading}
    onClick={onClick}
    data-testid={testId}
    className="w-full rounded-full py-4 text-sm tracking-[0.3em] uppercase transition-all"
    style={{
      background: disabled ? "rgba(255,255,255,0.06)" : `linear-gradient(135deg, ${VIOLET} 0%, #6A5EB0 100%)`,
      color: disabled ? "rgba(180,170,200,0.45)" : "white",
      fontFamily: "'Manrope', sans-serif", fontWeight: 500,
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : "0 8px 40px rgba(139,126,212,0.25)",
    }}
  >
    {loading ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> One moment…</span> : children}
  </button>
);

const SectionHead = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-md mx-auto text-center mb-10">
    <p className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: `${VIOLET}bb` }}>{eyebrow}</p>
    <h1 className="text-3xl mb-3" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.94)" }}>
      {title}
    </h1>
    {subtitle && (
      <p className="text-sm italic px-4" style={{ fontFamily: "'Spectral', serif", color: "rgba(220,210,230,0.55)" }}>
        {subtitle}
      </p>
    )}
  </div>
);

// ─── Step 1 — recipient ────────────────────────
const StepRecipient = ({ data, types, onNext }) => {
  const [name, setName] = useState(data.recipient_name || "");
  const [type, setType] = useState(data.recipient_type || "Friend");
  return (
    <motion.div key="s1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-6 pb-24" data-testid="gift-step-recipient">
      <SectionHead eyebrow="Step 1 of 6" title="Who is this moment for?" />
      <div className="max-w-md mx-auto space-y-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: `${VIOLET}bb` }}>Recipient name</label>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="e.g. Maya"
                 data-testid="gift-recipient-name"
                 className="w-full bg-transparent outline-none text-lg rounded-xl border px-4 py-3"
                 style={{ borderColor: `${VIOLET}3d`, background: "rgba(255,255,255,0.03)", color: "rgba(245,240,230,0.92)", fontFamily: "'Spectral', serif" }} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: `${VIOLET}bb` }}>Relationship</p>
          <div className="grid grid-cols-3 gap-2">
            {types.map((t) => {
              const active = type === t;
              return (
                <button key={t} onClick={() => setType(t)} data-testid={`gift-type-${t.toLowerCase()}`}
                        className="rounded-xl border py-2.5 text-sm transition-all"
                        style={{
                          background: active ? `${VIOLET}1a` : "rgba(255,255,255,0.03)",
                          borderColor: active ? `${VIOLET}88` : `${VIOLET}2a`,
                          color: active ? VIOLET : "rgba(245,240,230,0.85)",
                          fontFamily: "'Playfair Display', serif",
                        }}>{t}</button>
              );
            })}
          </div>
        </div>
        <PrimaryButton disabled={!name.trim()} testId="gift-s1-continue"
                       onClick={() => onNext({ recipient_name: name.trim(), recipient_type: type })}>
          Continue
        </PrimaryButton>
      </div>
    </motion.div>
  );
};

// ─── Step 2 — intention ────────────────────────
const StepIntention = ({ data, intentions, onNext }) => {
  const [id, setId] = useState(data.intention_id || "loved");
  const [customLabel, setCustomLabel] = useState(data.intention_custom_label || "");
  const chosen = intentions.find((i) => i.id === id);
  return (
    <motion.div key="s2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-6 pb-24" data-testid="gift-step-intention">
      <SectionHead eyebrow="Step 2 of 6" title="What would you love them to feel?" />
      <div className="max-w-md mx-auto grid gap-3">
        {intentions.map((it) => {
          const active = id === it.id;
          return (
            <button key={it.id} onClick={() => setId(it.id)} data-testid={`gift-intention-${it.id}`}
                    className="text-left rounded-2xl border px-5 py-4 transition-all"
                    style={{
                      background: active ? `${VIOLET}14` : "rgba(255,255,255,0.025)",
                      borderColor: active ? `${VIOLET}88` : `${VIOLET}1f`,
                    }}>
              <p className="text-lg" style={{ fontFamily: "'Playfair Display', serif", color: active ? VIOLET : "rgba(245,240,230,0.92)" }}>
                {it.label}
              </p>
              <p className="text-xs mt-1" style={{ color: "rgba(220,210,230,0.55)" }}>{it.subtitle}</p>
            </button>
          );
        })}
        {id === "custom" && (
          <input value={customLabel} onChange={(e) => setCustomLabel(e.target.value.slice(0, 40))}
                 placeholder="Name your intention (e.g. Brave)"
                 data-testid="gift-intention-custom-label"
                 className="bg-transparent outline-none text-base rounded-xl border px-4 py-3 mt-2"
                 style={{ borderColor: `${VIOLET}3d`, background: "rgba(255,255,255,0.03)", color: "rgba(245,240,230,0.92)" }} />
        )}
        <PrimaryButton testId="gift-s2-continue"
                       onClick={() => onNext({
                         intention_id: id,
                         intention_custom_label: id === "custom" ? customLabel.trim() : null,
                         defaultAffirmations: chosen?.affirmations || [],
                       })}>
          Continue
        </PrimaryButton>
      </div>
    </motion.div>
  );
};

// ─── Step 3 — personal line ────────────────────
const StepPersonal = ({ data, onNext }) => {
  const [line, setLine] = useState(data.personal_line || "");
  const examples = [
    "I just want you to know how much I appreciate you.",
    "I see how strong you are.",
    "I am so proud of you.",
    "You mean so much to me.",
    "I believe in you.",
  ];
  return (
    <motion.div key="s3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-6 pb-24" data-testid="gift-step-personal">
      <SectionHead eyebrow="Step 3 of 6" title="Add one personal line"
                   subtitle="Keep it simple and from the heart. This is what makes the moment feel real." />
      <div className="max-w-md mx-auto space-y-5">
        <textarea value={line} onChange={(e) => setLine(e.target.value.slice(0, 280))} rows={3}
                  placeholder="What do you want them to know?"
                  data-testid="gift-personal-line"
                  className="w-full bg-transparent outline-none text-base rounded-xl border px-4 py-3"
                  style={{ borderColor: `${VIOLET}3d`, background: "rgba(255,255,255,0.03)", color: "rgba(245,240,230,0.92)", fontFamily: "'Spectral', serif" }} />
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: `${VIOLET}bb` }}>Examples — tap to use</p>
          <div className="space-y-2">
            {examples.map((ex, i) => (
              <button key={i} onClick={() => setLine(ex)} data-testid={`gift-example-${i}`}
                      className="w-full text-left text-sm px-4 py-2.5 rounded-xl border transition-all"
                      style={{ background: "rgba(255,255,255,0.025)", borderColor: `${VIOLET}1f`, color: "rgba(220,210,230,0.75)", fontStyle: "italic", fontFamily: "'Spectral', serif" }}>
                {ex}
              </button>
            ))}
          </div>
        </div>
        <PrimaryButton disabled={!line.trim()} testId="gift-s3-continue" onClick={() => onNext({ personal_line: line.trim() })}>
          Continue
        </PrimaryButton>
      </div>
    </motion.div>
  );
};

// ─── Step 4 — affirmations ─────────────────────
const StepAffirmations = ({ data, onNext }) => {
  const [list, setList] = useState(() =>
    (data.affirmations && data.affirmations.length ? data.affirmations : data.defaultAffirmations || []).slice(0, 7)
  );
  const [editingIdx, setEditingIdx] = useState(null);
  const [tempText, setTempText] = useState("");

  const remove = (i) => setList((l) => l.filter((_, idx) => idx !== i));
  const startEdit = (i) => { setEditingIdx(i); setTempText(list[i]); };
  const saveEdit = () => {
    if (editingIdx === null) return;
    const cleaned = tempText.trim().slice(0, 120);
    if (!cleaned) return setEditingIdx(null);
    setList((l) => l.map((v, i) => (i === editingIdx ? cleaned : v)));
    setEditingIdx(null);
  };
  const add = () => setList((l) => (l.length >= 7 ? l : [...l, ""]));

  const canContinue = list.filter((a) => a.trim()).length >= 3;

  return (
    <motion.div key="s4" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-6 pb-24" data-testid="gift-step-affirmations">
      <SectionHead eyebrow="Step 4 of 6" title="Choose five affirmations"
                   subtitle={`These will be read as "You are…". Tap to edit, or add your own. 3–7 allowed.`} />
      <div className="max-w-md mx-auto space-y-3">
        {list.map((a, i) => {
          const isEditing = editingIdx === i;
          return (
            <div key={i} className="flex items-center gap-2 rounded-xl border px-4 py-3"
                 style={{ background: "rgba(255,255,255,0.025)", borderColor: `${VIOLET}1f` }}
                 data-testid={`gift-aff-row-${i}`}>
              {isEditing ? (
                <>
                  <input autoFocus value={tempText}
                         onChange={(e) => setTempText(e.target.value.slice(0, 120))}
                         onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                         onBlur={saveEdit}
                         className="flex-1 bg-transparent outline-none text-base"
                         style={{ color: "rgba(245,240,230,0.92)", fontFamily: "'Spectral', serif" }}
                         data-testid={`gift-aff-edit-${i}`} />
                  <button onClick={saveEdit} data-testid={`gift-aff-save-${i}`}
                          className="p-1.5 rounded-full" style={{ background: `${VIOLET}22` }}>
                    <Check size={14} color={VIOLET} />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => startEdit(i)} className="flex-1 text-left text-base"
                          style={{ color: "rgba(245,240,230,0.9)", fontFamily: "'Spectral', serif" }}
                          data-testid={`gift-aff-text-${i}`}>
                    {a || <span style={{ color: "rgba(200,190,220,0.4)" }}>Tap to write…</span>}
                  </button>
                  <button onClick={() => startEdit(i)} className="p-1.5 rounded-full hover:bg-white/5"
                          data-testid={`gift-aff-editbtn-${i}`}>
                    <Pencil size={13} color={`${VIOLET}cc`} />
                  </button>
                  <button onClick={() => remove(i)} className="p-1.5 rounded-full hover:bg-white/5"
                          data-testid={`gift-aff-delete-${i}`}>
                    <Trash2 size={13} color="rgba(255,120,120,0.55)" />
                  </button>
                </>
              )}
            </div>
          );
        })}
        {list.length < 7 && (
          <button onClick={add} data-testid="gift-aff-add"
                  className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm"
                  style={{ borderColor: `${VIOLET}44`, color: `${VIOLET}cc`, fontFamily: "'Manrope', sans-serif" }}>
            <Plus size={14} /> Add your own
          </button>
        )}
        <PrimaryButton disabled={!canContinue} testId="gift-s4-continue"
                       onClick={() => onNext({ affirmations: list.map((a) => a.trim()).filter(Boolean) })}>
          Continue · {list.filter((a) => a.trim()).length} of 5
        </PrimaryButton>
      </div>
    </motion.div>
  );
};

// ─── Step 5 — music ────────────────────────────
const StepMusic = ({ data, styles, onNext }) => {
  const [id, setId] = useState(data.music_style_id || styles[0]?.id);
  const playingRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  const preview = (s) => {
    if (playingRef.current) { try { playingRef.current.pause(); } catch (_) {} playingRef.current = null; }
    if (playingId === s.id) { setPlayingId(null); return; }
    const a = new Audio(`${API}${s.url}`);
    a.volume = 0.55;
    a.play().catch(() => {});
    playingRef.current = a;
    setPlayingId(s.id);
    setTimeout(() => { if (playingRef.current === a) { try { a.pause(); } catch (_) {} setPlayingId(null); } }, 12000);
  };
  useEffect(() => () => { if (playingRef.current) { try { playingRef.current.pause(); } catch (_) {} } }, []);

  return (
    <motion.div key="s5" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-6 pb-24" data-testid="gift-step-music">
      <SectionHead eyebrow="Step 5 of 6" title="Choose the feeling of the music" subtitle="Tap ▶ to preview." />
      <div className="max-w-md mx-auto grid gap-3">
        {styles.map((s) => {
          const active = id === s.id;
          const playing = playingId === s.id;
          return (
            <div key={s.id} className="flex items-center gap-3 rounded-2xl border px-4 py-3.5 transition-all"
                 style={{ background: active ? `${VIOLET}14` : "rgba(255,255,255,0.025)",
                          borderColor: active ? `${VIOLET}88` : `${VIOLET}1f` }}
                 onClick={() => setId(s.id)}
                 data-testid={`gift-music-${s.id}`}>
              <button onClick={(e) => { e.stopPropagation(); preview(s); }}
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `${VIOLET}22`, border: `1px solid ${VIOLET}44` }}
                      data-testid={`gift-music-preview-${s.id}`}>
                {playing ? <Pause size={15} color="white" /> : <Play size={15} color="white" className="ml-0.5" />}
              </button>
              <div className="flex-1">
                <p className="text-base" style={{ fontFamily: "'Playfair Display', serif", color: active ? VIOLET : "rgba(245,240,230,0.92)" }}>
                  {s.label}
                </p>
                <p className="text-xs" style={{ color: "rgba(220,210,230,0.55)" }}>{s.subtitle}</p>
              </div>
              {active && <Check size={16} color={VIOLET} />}
            </div>
          );
        })}
        <PrimaryButton testId="gift-s5-continue" onClick={() => { if (playingRef.current) try { playingRef.current.pause(); } catch (_) {}; onNext({ music_style_id: id }); }}>
          Create Moment
        </PrimaryButton>
      </div>
    </motion.div>
  );
};

// ─── Step 6 — preview + share ──────────────────
const StepPreview = ({ gift, onRegen, onGenerate, generating, onCover, uploadingCover }) => {
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullShare = `${origin}${gift.share_url}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(fullShare); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (_) {}
  };

  const sendViaNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `A Reclaim Moment for ${gift.recipient_name}`,
          text: `I made you a Reclaim Moment — ${gift.intention_label}. Give it a quiet minute.`,
          url: fullShare,
        });
      } else {
        await copy();
      }
    } catch (_) {}
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    onCover(f);
    e.target.value = "";
  };

  return (
    <motion.div key="s6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-2 pb-24" data-testid="gift-step-preview">
      <SectionHead eyebrow={`For ${gift.recipient_name}`} title="Your moment is ready"
                   subtitle={`Intention · ${gift.intention_label}`} />

      <div className="max-w-md mx-auto space-y-4">
        {/* Cover image — optional personal photo that becomes the blurred
            backdrop on the recipient landing page. Feels like a handwritten card. */}
        <div className="rounded-3xl border overflow-hidden"
             style={{ background: "rgba(255,255,255,0.03)", borderColor: `${VIOLET}2f` }}
             data-testid="gift-cover-block">
          {gift.cover_image_url ? (
            <div className="relative aspect-[16/10]">
              <img src={`${API}${gift.cover_image_url}`} alt="" className="absolute inset-0 w-full h-full object-cover"
                   data-testid="gift-cover-preview" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,18,0) 40%, rgba(10,10,18,0.78) 100%)" }} />
              <div className="absolute bottom-0 inset-x-0 p-3 flex items-center justify-between gap-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploadingCover}
                        className="text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full"
                        style={{ background: "rgba(255,255,255,0.1)", color: "white", backdropFilter: "blur(6px)" }}
                        data-testid="gift-cover-change">Change</button>
                <button onClick={() => onCover(null)} disabled={uploadingCover}
                        className="text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full"
                        style={{ background: "rgba(255,255,255,0.1)", color: "white", backdropFilter: "blur(6px)" }}
                        data-testid="gift-cover-remove">Remove</button>
              </div>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={uploadingCover}
                    className="w-full flex items-center gap-3 px-5 py-4"
                    data-testid="gift-cover-add">
              <div className="w-10 h-10 rounded-full flex items-center justify-center"
                   style={{ background: `${VIOLET}22`, border: `1px solid ${VIOLET}44` }}>
                {uploadingCover ? <Loader2 size={15} className="animate-spin" color="white" /> : <Plus size={15} color="white" />}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.92)" }}>
                  Add a personal photo
                </p>
                <p className="text-[11px]" style={{ color: "rgba(220,210,230,0.55)" }}>
                  Optional · becomes the soft background of their page
                </p>
              </div>
              <ChevronRight size={14} color={`${VIOLET}cc`} />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick}
                 data-testid="gift-cover-file" />
        </div>

        <div className="rounded-3xl border p-6"
             style={{ background: "rgba(255,255,255,0.04)", borderColor: `${VIOLET}2f` }}>
          <p className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: `${VIOLET}bb` }}>Personal message</p>
          <p className="text-base italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.92)" }}>
            &ldquo;{gift.personal_line}&rdquo;
          </p>
          <div className="my-5 h-px w-12" style={{ background: `${VIOLET}55` }} />
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: `${VIOLET}bb` }}>Affirmations</p>
          <ul className="space-y-2">
            {gift.affirmations.map((a, i) => (
              <li key={i} className="text-base" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.88)" }}>
                · {a}
              </li>
            ))}
          </ul>
        </div>

        {gift.audio_url ? (
          <div className="rounded-2xl border p-4 flex items-center gap-3"
               style={{ background: "rgba(255,255,255,0.035)", borderColor: `${VIOLET}2f` }}>
            <audio src={`${API}${gift.audio_url}`} controls className="flex-1" data-testid="gift-audio-player" />
          </div>
        ) : (
          <button onClick={onGenerate} disabled={generating}
                  data-testid="gift-generate-audio"
                  className="w-full rounded-2xl border py-4 text-sm uppercase tracking-[0.3em]"
                  style={{ background: `${VIOLET}0a`, borderColor: `${VIOLET}55`, color: VIOLET, fontFamily: "'Manrope', sans-serif", cursor: generating ? "wait" : "pointer" }}>
            {generating ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Composing audio (45–60s)…</span> : (<><Sparkles size={14} className="inline mr-2" /> Generate the audio</>)}
          </button>
        )}

        <PrimaryButton onClick={sendViaNative} testId="gift-send">
          <Share2 size={15} className="inline mr-2" /> Send Moment
        </PrimaryButton>

        <button onClick={copy} data-testid="gift-copy-link"
                className="w-full rounded-full border py-3 text-xs uppercase tracking-[0.32em] flex items-center justify-center gap-2"
                style={{ background: "transparent", borderColor: `${VIOLET}44`, color: "rgba(220,210,230,0.85)", fontFamily: "'Manrope', sans-serif" }}>
          {copied ? <><Check size={13} /> Link copied</> : <><Copy size={13} /> Copy link</>}
        </button>
        <p className="text-[11px] text-center break-all" style={{ color: "rgba(200,190,220,0.45)" }}>
          {fullShare}
        </p>
        <button onClick={onRegen} data-testid="gift-edit"
                className="w-full rounded-full py-3 text-[11px] uppercase tracking-[0.3em]"
                style={{ background: "transparent", color: "rgba(180,170,200,0.6)", fontFamily: "'Manrope', sans-serif" }}>
          Edit from the start
        </button>
      </div>
    </motion.div>
  );
};


// ─── Page wrapper ───────────────────────────────
export default function GiftCreatePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [intentions, setIntentions] = useState([]);
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [musicStyles, setMusicStyles] = useState([]);
  const [gift, setGift] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJson("/api/gift/intentions", { auth: false }).then((d) => {
      setIntentions(d.intentions); setRelationshipTypes(d.relationship_types);
    }).catch(() => setError("Could not load intentions"));
    fetchJson("/api/gift/music-styles", { auth: false }).then((d) => setMusicStyles(d.styles))
      .catch(() => setError("Could not load music styles"));
  }, []);

  const back = () => {
    if (step === 1) return navigate(-1);
    // Record sub-screen back → voice choice
    if (step === 6.5) return setStep(6);
    setStep((s) => Math.max(1, Math.floor(s) - 1));
  };
  const close = () => navigate(-1);

  const submitCreate = async (patch) => {
    const merged = { ...data, ...patch };
    setData(merged);
    setCreating(true);
    setError(null);
    try {
      const res = await fetchJson("/api/gift/create", {
        method: "POST",
        body: JSON.stringify({
          recipient_name: merged.recipient_name,
          recipient_type: merged.recipient_type,
          intention_id: merged.intention_id,
          intention_custom_label: merged.intention_custom_label || null,
          personal_line: merged.personal_line,
          affirmations: merged.affirmations,
          music_style_id: merged.music_style_id,
        }),
      });
      setGift(res);
      setStep(6);
    } catch (e) {
      // Surface the real reason so a user can see auth / validation / server errors.
      const msg =
        e?.status === 401 || e?.status === 403
          ? "Your session has expired. Please sign in again and try once more."
          : e?.status === 422
            ? "Some fields need a quick fix — go back and check each step."
            : `We couldn't save your moment. (${e?.message || "server error"}) Please try again.`;
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const generateAudio = async () => {
    if (!gift) return;
    setGenerating(true);
    try {
      const res = await fetchJson(`/api/gift/${gift.gift_id}/generate-audio`, { method: "POST" });
      setGift(res);
    } catch (_) {
      setError("Audio generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  // Upload or clear the sender's personal cover photo. A null `file`
  // signals "remove existing cover".
  const onCover = async (file) => {
    if (!gift) return;
    setUploadingCover(true);
    setError(null);
    try {
      if (file === null) {
        const r = await fetch(`${API}/api/gift/${gift.gift_id}/cover`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (r.ok) setGift(await r.json());
      } else {
        const fd = new FormData();
        fd.append("file", file);
        const r = await fetch(`${API}/api/gift/${gift.gift_id}/cover`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token()}` },
          body: fd,
        });
        if (!r.ok) throw new Error(await r.text().catch(() => `${r.status}`));
        setGift(await r.json());
      }
    } catch (e) {
      setError("We couldn't attach that photo. Try a JPG or PNG under 8 MB.");
    } finally {
      setUploadingCover(false);
    }
  };

  const next = (patch, isFinal = false) => {
    const merged = { ...data, ...patch };
    setData(merged);
    if (isFinal) return submitCreate(patch);
    setStep((s) => s + 1);
  };

  const title = ["", "Recipient", "Intention", "Personal line", "Affirmations", "Music", "Voice", "Preview"][step] || "";

  return (
    <GiftFrame eyebrow="Gift a Moment" title={title} onBack={back} onClose={close}>
      {error && (
        <div className="mx-6 my-2 px-4 py-2 rounded-lg text-xs" data-testid="gift-error"
             style={{ background: "rgba(255,80,80,0.1)", color: "#ffb3b3", border: "1px solid rgba(255,80,80,0.25)" }}>
          {error}
        </div>
      )}
      <AnimatePresence mode="wait">
        {step === 1 && <StepRecipient key="s1" data={data} types={relationshipTypes} onNext={(p) => next(p)} />}
        {step === 2 && <StepIntention key="s2" data={data} intentions={intentions} onNext={(p) => next(p)} />}
        {step === 3 && <StepPersonal key="s3" data={data} onNext={(p) => next(p)} />}
        {step === 4 && <StepAffirmations key="s4" data={data} onNext={(p) => next(p)} />}
        {step === 5 && <StepMusic key="s5" data={data} styles={musicStyles} onNext={(p) => next(p, true)} />}
        {step === 6 && gift && (
          <VoiceChoice
            key="voice-choice"
            onMichael={async () => { setStep(7); await generateAudio(); }}
            onRecord={() => setStep(6.5)}
          />
        )}
        {step === 6.5 && gift && (
          <RecordScreen
            key="record"
            gift={gift}
            onBack={() => setStep(6)}
            onUploaded={(updated) => { setGift(updated); setStep(7); }}
          />
        )}
        {step === 7 && gift && (
          <StepPreview key="s7" gift={gift}
                       onRegen={() => {
                         // Reset FULL form state so the next gift starts fresh.
                         setGift(null);
                         setData({});
                         setError(null);
                         setStep(1);
                       }}
                       onGenerate={generateAudio} generating={generating}
                       onCover={onCover} uploadingCover={uploadingCover} />
        )}
        {creating && step !== 6 && step !== 7 && (
          <motion.div key="creating" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="text-center py-16" data-testid="gift-creating">
            <Loader2 className="animate-spin mx-auto mb-3" color={VIOLET} size={22} />
            <p className="text-sm" style={{ color: "rgba(220,210,230,0.7)", fontFamily: "'Spectral', serif" }}>
              Composing your moment…
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </GiftFrame>
  );
}
